'use client';

import { cn } from '@/utils/cn';
import * as Headless from '@headlessui/react';
import React, { useState } from 'react';
import AccountNav from '../account/AccountNav';
import MobileAccountNav from '../account/MobileAccountNav';
import { NavbarItem } from './navbar';

function OpenMenuIcon() {
  return (
    <svg data-slot="icon" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M2 6.75C2 6.33579 2.33579 6 2.75 6H17.25C17.6642 6 18 6.33579 18 6.75C18 7.16421 17.6642 7.5 17.25 7.5H2.75C2.33579 7.5 2 7.16421 2 6.75ZM2 13.25C2 12.8358 2.33579 12.5 2.75 12.5H17.25C17.6642 12.5 18 12.8358 18 13.25C18 13.6642 17.6642 14 17.25 14H2.75C2.33579 14 2 13.6642 2 13.25Z" />
    </svg>
  );
}

function CloseMenuIcon() {
  return (
    <svg data-slot="icon" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  );
}

function MobileSidebar({
  open,
  close,
  children
}: React.PropsWithChildren<{ open: boolean; close: () => void }>) {
  return (
    <Headless.Dialog open={open} onClose={close} className="lg:hidden">
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 transition data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <Headless.DialogPanel
        transition
        className="fixed inset-y-0 w-full max-w-80 p-2 transition duration-300 ease-in-out data-[closed]:-translate-x-full"
      >
        <div className="flex h-full flex-col rounded-lg bg-white shadow-sm ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
          <div className="-mb-3 px-4 pt-3">
            <Headless.CloseButton as={NavbarItem} aria-label="Close navigation">
              <CloseMenuIcon />
            </Headless.CloseButton>
          </div>
          {children}
        </div>
      </Headless.DialogPanel>
    </Headless.Dialog>
  );
}

export function SidebarLayout({ children }: React.PropsWithChildren<{}>) {
  let [showSidebar, setShowSidebar] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className={cn('flex flex-col h-full')}>
      <div className="relative isolate flex min-h-svh w-full bg-white dark:bg-zinc-900 max-lg:flex-col lg:bg-muted dark:lg:bg-zinc-950">
        {/* Sidebar on desktop */}
        <div
          className={cn(
            'fixed inset-y-2 left-0 w-full max-lg:hidden overflow-hidden transition-[max-width] duration-300 ease-in-out',
            collapsed ? 'max-w-16' : 'max-w-64'
          )}
        >
          <AccountNav collapsed={collapsed} setCollapsed={setCollapsed} />
        </div>

        {/* Sidebar on mobile */}
        <MobileSidebar open={showSidebar} close={() => setShowSidebar(false)}>
          <MobileAccountNav />
        </MobileSidebar>

        {/* Navbar on mobile */}
        <header className="flex items-center px-4 lg:hidden">
          <div className="py-2.5">
            <NavbarItem
              onClick={() => setShowSidebar(true)}
              aria-label="Open navigation"
            >
              <OpenMenuIcon />
            </NavbarItem>
          </div>
          <div className="flex min-w-0 flex-1 justify-center">
            <img src="/brand/logo.svg" alt="Corvus" className="h-[20px]" />
          </div>
        </header>

        {/* Content */}
        <main
          className={cn(
            'flex flex-1 flex-col lg:min-w-0 lg:pr-2 transition-[padding-left] duration-300 ease-in-out',
            collapsed ? 'lg:pl-16' : 'lg:pl-64'
            // showBanner && 'lg:pt-12'
          )}
        >
          <div className="grow border-l border-l-zinc-200 bg-background lg:p-0">
            <div className={cn('mx-auto max-w-8xl h-full')}>{children}</div>
          </div>
        </main>
      </div>
    </div>
  );
}
